/**
 * Shrinks a photo before it is uploaded.
 *
 * A Medicare or Health Care card photographed on a modern phone is 4-12 MB. Cloud Run rejects any
 * request over 32 MB before it reaches the backend — with no log entry and no CORS headers, so the
 * page only shows "Unable to reach the server" — and every one of those megabytes is then stored
 * per parent, per card, forever.
 *
 * A card only has to be readable, so 1600px on the long edge at JPEG quality 0.82 is plenty: it
 * lands around 200-400 KB, roughly a twentieth of the original.
 */

export const MAX_EDGE = 1600;
export const QUALITY = 0.82;

/** Scale to fit inside maxEdge, keeping the aspect ratio. Never enlarges a smaller image. */
export const fitWithin = (width, height, maxEdge = MAX_EDGE) => {
    const longest = Math.max(width, height);
    if (!longest || longest <= maxEdge) return { width, height };

    const scale = maxEdge / longest;
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
};

/** Photos only. A PDF or anything else is passed through untouched. */
export const isCompressibleImage = (file) =>
    Boolean(file) && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);

/** card.png -> card.jpg, since the compressed bytes are always JPEG. */
export const jpegName = (name = 'upload') => `${String(name).replace(/\.[^./\\]+$/, '')}.jpg`;

const defaultDeps = {
    createImageBitmap: (...args) => window.createImageBitmap(...args),
    createCanvas: (width, height) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        return canvas;
    },
    toBlob: (canvas, type, quality) =>
        new Promise((resolve) => canvas.toBlob(resolve, type, quality)),
};

/**
 * Returns a smaller File, or the original when it cannot be improved.
 *
 * Never throws: a browser that cannot decode the image, or a "compressed" result that came out
 * larger than the original, falls back to uploading what the parent chose. Losing the upload
 * would be a worse outcome than sending a large file.
 */
export async function compressImage(file, { maxEdge = MAX_EDGE, quality = QUALITY, deps = defaultDeps } = {}) {
    if (!isCompressibleImage(file)) return file;

    try {
        const bitmap = await deps.createImageBitmap(file);
        const { width, height } = fitWithin(bitmap.width, bitmap.height, maxEdge);

        const canvas = deps.createCanvas(width, height);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
        if (bitmap.close) bitmap.close();

        const blob = await deps.toBlob(canvas, 'image/jpeg', quality);
        if (!blob || blob.size >= file.size) return file;

        return new File([blob], jpegName(file.name), {
            type: 'image/jpeg',
            lastModified: Date.now(),
        });
    } catch (error) {
        console.warn('Could not shrink the image, uploading it as it is:', error);
        return file;
    }
}
